import * as React from "react";
import { useQuery } from "@apollo/react-hooks";
import formatDate from "dateformat";
import { COVID_CASES_BY_COUNTRY } from "../query";
import DailyCasesChart from "../../../../ui-library/daily-cases-chart/daily-cases-chart";
import {
  formatNumber,
  formatNumberDisplay,
} from "../../../../../utils/format-number";
import {
  CovidCaseByCountry,
  CovidCaseByCountryVariables,
  CovidCaseByCountry_covidCaseOwidByCountry_data,
  CovidCaseByCountry_covidCaseOwidByCountry_latest,
} from "../../../../../apollo-types/CovidCaseByCountry";
import BenchmarkCountrySuggest from "../country-suggest/benchmark-country-suggest";
import { AskAboutContext } from "../../../../../site-context/ask-about-context";
import CardSubHeading from "../../../../ui-library/content-card/card-sub-heading";
import GovernmentResponseVsCases from "../government-response-timeline/government-response-vs-cases";
import { useQueryGovResTimeline } from "../../../../../hooks/live-queries/use-query-gov-res-timeline/use-query-gov-res-timeline";
import Row from "../../../../ui-library/column-system/row";
import Column from "../../../../ui-library/column-system/column";
import { useQueryGovResSeries } from "../../../../../hooks/live-queries/use-query-gov-res-timeline/use-query-gov-res-series";
import { FAKE_LAST_DAY } from "../../../../../constants/fake-last-day-data";
import { grid } from "../../../../design-tokens/grid";
import { Covid19AskCountriesQueryQuery } from "../../../../../../graphql-types";
import USStatesControlSnapshots from "../countries-control-snapshots/us-states-control-snapshots";
import USStatesInfectionRates from "../countries-control-snapshots/us-states-infection-rates";
import USFirePlot from "../countries-fireplots/us-fireplots";
import AUFirePlot from "../countries-fireplots/au-fireplots";
import CAFirePlot from "../countries-fireplots/ca-fireplots";
import InfectionRateCard from "./infection-rate-card/infection-rate-card";
import HowWellSituationCard from "./how-well-situation-card/how-well-situation-card";
import CountrySnapshotCard from "./country-snapshot-card";
import {
  keysToPlotConfirmed,
  keysToPlotActive,
  keysToPlotRecovered,
  keysToPlotDeath,
  keysToPlotTests,
  keysToPlotFatality,
} from "./graph-keys";
import DailyCaseCard from "./daily-case-card";
import ContentCard from "../../../../ui-library/content-card/content-card";
import CardContent from "../../../../ui-library/content-card/card-content";
import CardHeading from "../../../../ui-library/content-card/card-heading";
import SourceLink from "../../../../ui-library/source-link/source-link";
import { COVID_SOURCES } from "../../../../../constants/sources";
import { EventCategory } from "../../../../../utils/analytics";
import CountriesSituationCTAs from "../../../covid-19-home/countries-situation-ctas/countries-situation-ctas";
import Container from "../../../../ui-library/container/container";

interface DailyCaseByCountryProps {
  intentName: string;
  country: string;
  countryName: string;
  prefetchedCovidData?: Covid19AskCountriesQueryQuery["askAboutQuery"]["covidCaseOwidByCountry"];
}

const DailyCaseByCountry: React.FC<DailyCaseByCountryProps> = ({
  intentName,
  country,
  countryName,
  prefetchedCovidData,
}) => {
  const {
    store: {
      isMobile,
      userCountry: [{ benchmarkCountry }],
    },
  } = React.useContext(AskAboutContext);

  const { data, loading: liveDataLoading } = useQuery<
    CovidCaseByCountry,
    CovidCaseByCountryVariables
  >(COVID_CASES_BY_COUNTRY, {
    variables: { country },
  });

  const loading = liveDataLoading && !prefetchedCovidData;

  const { data: benchmarkData } = useQuery<
    CovidCaseByCountry,
    CovidCaseByCountryVariables
  >(COVID_CASES_BY_COUNTRY, {
    variables: { country: benchmarkCountry?.country || "" },
    skip: !benchmarkCountry?.country,
  });

  const { loading: govResLoading, responseTimeline } = useQueryGovResTimeline(
    country
  );
  const {
    loading: govResSeriesLoading,
    responseTimeSeries,
  } = useQueryGovResSeries(country);

  const covidData =
    data?.covidCaseOwidByCountry?.data ||
    (prefetchedCovidData?.data as
      | CovidCaseByCountry_covidCaseOwidByCountry_data[]
      | null);

  const covidBenchmarkData = benchmarkData?.covidCaseOwidByCountry?.data;

  const lastDayValue = (data?.covidCaseOwidByCountry?.latest ||
    prefetchedCovidData?.latest ||
    FAKE_LAST_DAY) as CovidCaseByCountry_covidCaseOwidByCountry_latest;

  const headingDetail = `Last updated ${formatDate(
    lastDayValue.date,
    "dd mmm yyyy"
  )}`;

  const testsUnits =
    lastDayValue.testsUnits &&
    lastDayValue.testsUnits !== "units unclear" &&
    lastDayValue.testsUnits !== "inconsistent units (COVID Tracking Project)"
      ? ` ${lastDayValue.testsUnits}`
      : " tests conducted";

  const locationName = country === "World" ? "globally" : `in ${countryName}`;

  const howWellIsSituation = (
    <HowWellSituationCard
      countryName={countryName}
      data={covidData}
      lastDayValue={lastDayValue}
      benchmarkCountry={benchmarkCountry}
      covidBenchmarkData={covidBenchmarkData}
      loading={loading}
    />
  );

  const infectionRateCard = (
    <InfectionRateCard
      country={country}
      countryName={countryName}
      data={covidData}
      benchmarkCountry={benchmarkCountry}
      covidBenchmarkData={covidBenchmarkData}
      loading={loading}
    />
  );

  if (!covidData && !loading) {
    return (
      <>
        <ContentCard>
          <CardContent>
            <CardHeading>
              Sorry, we don't have data for {countryName}
            </CardHeading>
            <CardSubHeading>
              Our COVID-19 data is sourced from{" "}
              <a
                href="https://ourworldindata.org/"
                target="_blank"
                rel="noreferrer"
              >
                Our World in Data
              </a>
              . They currently don't have data for {countryName}
            </CardSubHeading>

            <SourceLink
              sources={[COVID_SOURCES.OWID]}
              isDataOnly={true}
              eventCategory={EventCategory.CountryPage}
            />
          </CardContent>
        </ContentCard>
        <Container>
          <CountriesSituationCTAs />
        </Container>
      </>
    );
  }

  return (
    <>
      <CountrySnapshotCard
        countryName={countryName}
        lastDayValue={lastDayValue}
        covidData={covidData}
        headingDetail={headingDetail}
        loading={loading}
      />

      <BenchmarkCountrySuggest />

      {!isMobile ? (
        <Row>
          <Column spanLg={6} spanMd={6} spanSm={12}>
            {howWellIsSituation}
          </Column>
          <Column spanLg={6} spanMd={6} spanSm={12}>
            {infectionRateCard}
          </Column>
        </Row>
      ) : (
        <>
          <div css={{ marginBottom: grid(3) }}>{howWellIsSituation}</div>
          <div css={{ marginBottom: grid(3) }}>{infectionRateCard}</div>
        </>
      )}

      <GovernmentResponseVsCases
        loading={govResLoading || govResSeriesLoading || loading}
        responseTimeline={responseTimeline?.data}
        responseTimeSeries={responseTimeSeries}
        countryName={countryName}
        data={covidData}
      />

      {country === "United States" && (
        <>
          <USStatesControlSnapshots top4Only={true} />
          <USStatesInfectionRates top4Only={true} />
          <USFirePlot />
        </>
      )}
      {country === "Australia" && <AUFirePlot />}
      {country === "Canada" && <CAFirePlot />}

      <DailyCaseCard
        id="dailyConfirm"
        intentName={intentName}
        answerIndex="Confirmed cases"
        heading={`${formatNumber(
          lastDayValue.totalCases || 0
        )} total cases ${locationName}`}
        headingDetail={headingDetail}
      >
        {!!covidData && (
          <DailyCasesChart data={covidData} keysToPlot={keysToPlotConfirmed} />
        )}
      </DailyCaseCard>

      {lastDayValue.totalActive !== null && (
        <DailyCaseCard
          id="dailyActive"
          intentName={intentName}
          answerIndex="Active cases"
          heading={`${formatNumber(
            lastDayValue.totalActive || 0
          )} active cases ${locationName}`}
          headingDetail={headingDetail}
        >
          {!!covidData && (
            <DailyCasesChart data={covidData} keysToPlot={keysToPlotActive} />
          )}
        </DailyCaseCard>
      )}

      {lastDayValue.totalRecovered !== null && (
        <DailyCaseCard
          id="dailyRecovered"
          intentName={intentName}
          answerIndex="Recovered cases"
          heading={`${formatNumber(
            lastDayValue.totalRecovered || 0
          )} recovered cases ${locationName}`}
          headingDetail={headingDetail}
        >
          {!!covidData && (
            <DailyCasesChart
              data={covidData}
              keysToPlot={keysToPlotRecovered}
            />
          )}
        </DailyCaseCard>
      )}

      {lastDayValue.totalTests !== null && (
        <DailyCaseCard
          id="dailyTested"
          intentName={intentName}
          answerIndex="Number of tests"
          heading={`${formatNumber(
            lastDayValue.totalTests || 0
          )}${testsUnits} ${locationName}`}
          headingDetail={headingDetail}
        >
          {!!covidData && (
            <DailyCasesChart data={covidData} keysToPlot={keysToPlotTests} />
          )}
        </DailyCaseCard>
      )}

      <DailyCaseCard
        id="dailyDeath"
        intentName={intentName}
        answerIndex="Death cases"
        heading={`${formatNumber(
          lastDayValue.totalDeaths || 0
        )} death cases ${locationName}`}
        headingDetail={headingDetail}
      >
        {!!covidData && (
          <>
            <CardSubHeading>
              Predicted death are based on China's case fatality rate
            </CardSubHeading>
            <DailyCasesChart data={covidData} keysToPlot={keysToPlotDeath} />
          </>
        )}
      </DailyCaseCard>

      <DailyCaseCard
        id="dailyFatalityRate"
        intentName={intentName}
        answerIndex="Fatality rate"
        heading={`${formatNumberDisplay(
          lastDayValue.fatalityRate || 0,
          true
        )}% case fatality rate ${locationName}`}
        headingDetail={headingDetail}
      >
        {!!covidData && (
          <DailyCasesChart
            data={covidData.map((d) => ({
              ...d,
              fatalityRate: d.fatalityRate
                ? Number((d.fatalityRate * 100).toFixed(1))
                : d.fatalityRate,
            }))}
            keysToPlot={keysToPlotFatality}
          />
        )}
      </DailyCaseCard>
    </>
  );
};

export default DailyCaseByCountry;
